import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.valeverdeambiental.com.br/api';

// Função para obter token CSRF dos cookies
const getCSRFToken = (): string | null => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Configuração do Axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Importante para manter cookies entre requisições
  timeout: 60000, // Aumentar timeout para 60 segundos
});

// Cache simples para requisições GET (desabilitado temporariamente para evitar erros TS)
// const requestCache = new Map<string, { data: any; timestamp: number; ttl: number }>();

// Log para debug da URL base

// API externa para consulta de CEP
const viaCepApi = axios.create({
  baseURL: 'https://viacep.com.br/ws',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Busca endereço através do CEP utilizando a API ViaCEP
 * @param cep - CEP no formato 00000000 ou 00000-000
 * @returns Dados do endereço conforme retornado pela API ViaCEP
 */
export const buscarEnderecoPorCep = async (cep: string): Promise<{
  logradouro: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
} | null> => {
  try {
    // Remove caracteres não numéricos
    const cepNumerico = cep.replace(/\D/g, '');
    
    if (cepNumerico.length !== 8) {
      throw new Error('CEP inválido');
    }
    
    const response = await viaCepApi.get(`/${cepNumerico}/json/`);
    
    if (response.data.erro) {
      throw new Error('CEP não encontrado');
    }
    
    // Mapeia os dados retornados para o formato usado na aplicação
    return {
      logradouro: response.data.logradouro,
      bairro: response.data.bairro,
      cidade: response.data.localidade,
      estado: response.data.uf,
      cep: cep
    };
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return null;
  }
};

// Request interceptor para autenticação
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Token ${token}`;
    }
    
    // Adicionar CSRF token para requisições não-GET
    if (config.method !== 'get' && config.headers) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
    
    // Cache desabilitado temporariamente
    // TODO: Reimplementar cache com tipagem correta
    
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor para erro handling
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Cache desabilitado temporariamente
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Cache functions desabilitadas temporariamente
export const clearCache = (pattern?: string) => {
  // TODO: Reimplementar quando cache estiver funcionando
  console.log('Cache clearing disabled temporarily');
};

// Função para login
export const login = async (username: string, password: string): Promise<boolean> => {
  try {
    // Primeiro, obter o token de autenticação
    
    // Usando o endpoint correto sem duplicar o /api/
    const loginURL = '/api-token-auth/';
    
    const response = await api.post(loginURL, { username, password });
    const { token, user } = response.data;
    
    if (token) {
      // Armazenar o token
      localStorage.setItem('authToken', token);
      
      // Usar os dados do usuário que já vieram na resposta do login
      if (user) {
        storeUser(user);
        return true;
      }
      
      // Se por algum motivo não vier os dados do usuário na resposta,
      // tentar buscar os dados completos do usuário (mas isso não deve acontecer)
      try {
        console.log('Tentando obter dados adicionais do usuário');
        const userResponse = await api.get('/api/users/me/');
        
        if (userResponse.status === 200) {
          console.log('Dados do usuário obtidos com sucesso');
          storeUser(userResponse.data);
        }
        
        return true;
      } catch (error) {
        console.error('Não foi possível obter dados adicionais do usuário, mas o login foi realizado com sucesso:', error);
        // Como já devemos ter os dados do usuário da resposta do login,
        // podemos ignorar esse erro e continuar
        return true;
      }
    }
    
    return false;
  } catch (error) {
    // Verificar explicitamente se é um erro 401 (credenciais inválidas)
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        console.error('Erro de autenticação (401): Credenciais inválidas');
        // Criar um erro com uma propriedade especial para identificar o tipo
        const authError = new Error('Usuário ou senha incorretos. Verifique suas credenciais.');
        (authError as any).isAuthError = true;
        throw authError;
      } else if (error.response?.status === 400) {
        throw new Error('Dados de login inválidos. Verifique os campos informados.');
      } else if (error.response?.status === 404) {
        throw new Error('Serviço de autenticação não encontrado. Entre em contato com o suporte.');
      } else if (error.response) {
        throw new Error(`Erro do servidor: ${error.response.status}. Tente novamente mais tarde.`);
      } else if (error.request) {
        throw new Error('Não foi possível se comunicar com o servidor. Verifique sua conexão.');
      }
    }
    
    console.error('Erro ao fazer login:', error);
    throw error;
  }
};

// Função para armazenar os dados do usuário
export const storeUser = (userData: any) => {
  if (!userData) return;
  
  // Usuário padronizado para uso no sistema
  const user = {
    id: userData.id,
    username: userData.username,
    first_name: userData.first_name,
    last_name: userData.last_name,
    name: `${userData.first_name} ${userData.last_name}`,
    // Normalizar o tipo/user_type (backend agora usa user_type)
    user_type: userData.user_type || userData.tipo,
    role: userData.user_type || userData.tipo, // Para compatibilidade
    tipo: userData.user_type || userData.tipo, // Para compatibilidade
    position: userData.position,
    cargo: userData.position, // Para compatibilidade
    team: userData.team,
    departamento: userData.team, // Para compatibilidade
    
    // Caminhos e URLs das imagens
    profile_picture: userData.profile_picture,
    profile_picture_thumb: userData.profile_picture_thumb,
    profile_picture_url: userData.profile_picture_url,
    profile_picture_thumb_url: userData.profile_picture_thumb_url,
    
    // Campos para compatibilidade
    avatar: userData.profile_picture_url || userData.profile_picture,
    profilePictureUrl: userData.profile_picture_url || userData.profile_picture
  };
  
  // Armazenar no localStorage
  localStorage.setItem('user', JSON.stringify(user));
  
  return user;
};

// Função para obter o usuário atual
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Erro ao analisar os dados do usuário:', error);
      return null;
    }
  }
  
  return null;
};

export const isAdmin = (): boolean => {
  const user = getCurrentUser();
  return user?.user_type === 'administrator' || user?.role === 'administrator';
};

export const logout = async (): Promise<void> => {
  try {
    const token = localStorage.getItem('authToken');
    
    // Se existe um token, tenta fazer logout na API
    if (token) {
      try {
        await api.post('/api/logout/');
      } catch (error) {
        console.error('Erro ao fazer logout na API:', error);
        // Continua mesmo se falhar na API
      }
    }
  } finally {
    // Sempre limpa os dados locais independente da resposta da API
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }
};

export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('authToken');
  return !!token;
};

export default api;
